import { useEffect, useRef } from 'react';

import { UseDismissListenersParams } from '../types/UseDismissListenersParams';

export const useDismissListeners = ({
  onClose,
  open,
  rootRef,
}: UseDismissListenersParams): void => {
  const onCloseRef = useRef(onClose);

  useEffect((): void => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect((): (() => void) | undefined => {
    if (!open) {
      return undefined;
    }

    let armed = false;
    // Armed on the next task so the click that opened the toast cannot close it
    const arm = setTimeout((): void => {
      armed = true;
    }, 0);

    const handleClick = (event: MouseEvent): void => {
      const { clientHeight, clientWidth } = document.documentElement;
      const onScrollbar =
        clientWidth < event.clientX || clientHeight < event.clientY;

      if (
        armed && // at least one task has run since the toast opened
        !onScrollbar && // the click was not on the page's scrollbar
        !rootRef.current?.contains(event.target as Node) // the click was outside the toast
      ) {
        onCloseRef.current();
      }
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!event.defaultPrevented && event.key === 'Escape') {
        onCloseRef.current();
      }
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);

    return (): void => {
      clearTimeout(arm);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);
};
